import type { DeliveryInstruction } from '@models/DeliveryInstruction';
import { ArrivalNotice } from '@models/next/ArrivalNotice';
import type {
  DeliveryRequestBatchSubmitAction,
  DeliveryRequestDateType,
  DeliveryRequestForm,
  DeliveryRequestListItem,
} from '@models/next/DeliveryRequest';
import { DeliveryRequestHelper } from '@models/next/DeliveryRequestHelper';
import type { PreAlertDetails } from '@models/next/PreAlert';
import {
  foldMessagesEither,
  makeMessagesEitherLeft,
  makeMessagesEitherRight,
  mergeMessagesEither,
  MessagesEither,
} from '@utils/errors';
import { createStrings } from '@utils/local';
import { formatDate, formatDateTime } from '@utils/types';
import dayjs from 'dayjs';
import { Either } from 'fp-ts/lib/Either';
import { uniqWith, isEqual } from 'lodash-es';

export const deliveryRequestStrings = createStrings({
  ja: {
    deliveryRequest: '配車依頼',
    deliveryRequestDocuments: '配車資料',
    deliveryRequestFile: '配車依頼書',
    deliveryCase: '配車案件',
    deliveryInfo: '配車情報',
    mailSubmission: '配車依頼',
    yokomochiCsvExport: '横持CSV出力',
    yokomochiSubmission: '横持配車依頼',
    deliverySentTitle: 'お届け完了処理',
    deliveryRequestNo: '配車番号',
    d2dNo: 'D2D番号',
    consignee: 'CNEE',
    pickupAddress: '集荷先',
    receiver: '納品先',
    truckType: '車種',
    truckCount: '数量',
    truckTypeAndCount: '車種・数量',
    memo: '配車メモ',
    truckCompany: 'トラック',
    pickupDate: '集荷日',
    deliveryDate: '納品日',
    deliveryDateTime: '納品時間',
    yokomochiPickupDate: '横持集荷日',
    yokomochiDeliveryDate: '横持納品日',
    sentDate: '配達日付',
    sentTime: '配達時間',
    deliverySentFile: '配達証明',
    status: '配車状況',
    driverInfo: 'ドライバー情報',
    deliveryStatus: '配達状態',
    doLessNo: 'D/Oレス番号',
    doLessStatus: 'D/O手配',
    moveOutNo: '搬出番号',
    moveOutStatus: '出庫手配',
    cargoQuantityWeightMeasurement: '総個数・総重量・総メジャー',
    cargoQuantity: '個数',
    cargoWeight: '重量',
    cargoMeasurement: '総メジャー',
    deliveryPrice: '配車金額',
    deliverySent: 'お届け完了',
    pod: '起点',
    creator: '作成者',
    createdAt: '作成時間',
    submittedAt: '送信時間',
    submissionReceiver: '送信先',
    changeLog: '変更履歴',
    deliverySummary: '納品内容',
    receiptPhoto: '受領書写真',
    deliveryDateTypeTime: '指定時間',
    myCases: '私の案件',
    getDeliverySummary: (item: any) =>
      `${item.pre_alert.quantity} ${item.pre_alert.packages.toUpperCase()}` +
      '/' +
      `${item.pre_alert.gross_weight} KGS` +
      '/' +
      `${item.pre_alert.measurement} CBM`,
    formatDeliveryDate: (item: any) => {
      switch (item.delivery_date_type) {
        case 'AM':
        case 'PM':
          return formatDate(item.delivery_date) + ' ' + item.delivery_date_type;
        case 'TIME':
          return formatDateTime(item.delivery_date);
      }
    },
    yokomochi: '横持ち',
    errorPreAlertNoEta: 'この案件のETAが確認されていません。',
    errorPreAlertNoArrivalNotice: 'この案件のArrival Noticeが作成されていません。',
    errorNoDeliveryRequestsSelected: '配車案件を選択してください。',
    errorPickupAddressInconsistent: (deliveryRequestNos: string[]) =>
      `選択された案件の集荷先には一貫性がありません。案件${deliveryRequestNos.join(
        '、',
      )}の集荷先が違います。`,
    errorDeliveryRequestsStatus: (deliveryRequestNos: string[]) =>
      `CSV送信済または横持送信済の案件のみを選択してください。案件${deliveryRequestNos.join(
        '、',
      )}の配車状況が違います。`,
    errorCannotSubmit: (deliveryRequestNo: string) =>
      `案件${deliveryRequestNo}のPreAlertはロックされていません。`,
    errorDeliveryRequestsAlreadySent: (deliveryRequestNos: string[]) =>
      `配達中の案件のみを選択してください。案件${deliveryRequestNos.join('')}はお届け完了です。`,
    warningCompleteDeliveryRequest: (deliveryRequestNo: string) =>
      `配車案件${deliveryRequestNo}は完了しましたか？`,
    errorDeliveryRequestsStatusComplete: (deliveryRequestNo: string) =>
      `案件${deliveryRequestNo}は配車完了ので、変更・追加・送信することはできません。`,
    errorCannotComplete: `「メール送信済既読」または「横持ち送信済」のみは「配車完了」にすることができます。`,
    errorPreAlertLedgerLocked:
      '案件のロックが施錠されています。そのため、配車案件の変更・追加・送信は行えません。解錠が必要な場合は、adminにご連絡ください。',
  },
});

export const allTruckTypes = [
  '混載便',
  '4T車',
  '10T車',
  '2T車',
  '増トン車',
  '4トン平',
  '10トン平',
  '4トンユニック',
  '10トンユニック',
  '3トン平',
  '3トンユニック',
  '7トン平',
  '7トンユニック',
  'トレーラー',
  "20' ドレー",
  "40' ドレー",
  'バン出しオントラック',
];

export const deliveryDateTypeOptions: { value: DeliveryRequestDateType; label: string }[] = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
  { value: 'TIME', label: '時間' },
];

export const resolveDeliveryRequestInitialOutput = (
  preAlert: PreAlertDetails,
  arrivalNotice: ArrivalNotice | null | undefined,
  deliveryInstruction: DeliveryInstruction,
): Either<{ messages: string[] }, DeliveryRequestForm> => {
  const messages: string[] = [];

  if (!preAlert.eta) {
    messages.push(deliveryRequestStrings.errorPreAlertNoEta);
  }

  if (!arrivalNotice) {
    messages.push(deliveryRequestStrings.errorPreAlertNoArrivalNotice);
  }

  // default：ETA +2 Days
  const pickup_date = dayjs(preAlert.eta).add(2, 'days').format('YYYY-MM-DD');

  const delivery_date_time =
    deliveryInstruction.preferred_delivery_date === '配達当日朝9:00' ? '09:00' : '';

  if (messages.length > 0) {
    return {
      _tag: 'Left',
      left: { messages },
    };
  }

  return {
    _tag: 'Right',
    right: {
      pickup_date,
      delivery_date_date: deliveryInstruction.preferred_delivery_date || '',
      delivery_date_time,
      delivery_date_type: 'TIME',
      pickup_address: {
        delivery_destination: arrivalNotice!.delivery_destination,
        delivery_destination_code: arrivalNotice!.delivery_destination_code,
        repository: arrivalNotice!.repository,
        address: arrivalNotice!.address,
        tel: arrivalNotice!.tel,
        fax: arrivalNotice!.fax,
        principal_name: arrivalNotice!.principal_name,
      },
      truck_type:
        deliveryInstruction.truck === '4T'
          ? '4T車'
          : deliveryInstruction.truck === '10T'
          ? '10T車'
          : deliveryInstruction.truck === '4Tx2'
          ? '4T車'
          : deliveryInstruction.truck === 'mix'
          ? '混載便'
          : '',
      truck_count: deliveryInstruction.truck === '4Tx2' ? 2 : 1,
      memo: '',
    },
  };
};

export const resolveDeliveryRequestBatchSubmitErrors = (
  deliveryRequests: DeliveryRequestListItem[],
  action: DeliveryRequestBatchSubmitAction,
): MessagesEither => {
  const messages: string[] = [];

  if (deliveryRequests.length === 0) {
    messages.push(deliveryRequestStrings.errorNoDeliveryRequestsSelected);
  }

  if (
    action === 'BATCH_CSV_SUBMIT' ||
    action === 'BATCH_MAIL_SUBMIT' ||
    action === 'BATCH_YOKOMOCHI_SUBMIT'
  ) {
    foldMessagesEither(
      mergeMessagesEither(...deliveryRequests.map((x) => () => DeliveryRequestHelper.canSubmit(x))),
      (e) => messages.push(...e.messages),
      () => {},
    );
  }

  if (action === 'BATCH_YOKOMOCHI_SUBMIT') {
    const uniqueByPickupAddress = uniqWith(deliveryRequests, (a, b) =>
      isEqual(a.pickup_address, b.pickup_address),
    );

    if (uniqueByPickupAddress.length > 1) {
      messages.push(
        deliveryRequestStrings.errorPickupAddressInconsistent(
          uniqueByPickupAddress.map((p) => p.delivery_request_no),
        ),
      );
    }

    const errorLastStatus = deliveryRequests.filter(
      (d) => DeliveryRequestHelper.getType(d) !== 'yokomochi',
    );

    if (errorLastStatus.length > 0) {
      messages.push(
        deliveryRequestStrings.errorDeliveryRequestsStatus(
          errorLastStatus.map((p) => p.delivery_request_no),
        ),
      );
    }
  }

  if (messages.length > 0) {
    return makeMessagesEitherLeft({ messages });
  }

  return makeMessagesEitherRight();
};

export const resolveDeliveryRequestBatchSentErrors = (
  deliveryRequests: DeliveryRequestListItem[],
): Either<{ messages: string[] }, void> => {
  const messages: string[] = [];

  if (deliveryRequests.length === 0) {
    messages.push(deliveryRequestStrings.errorNoDeliveryRequestsSelected);
  }

  const alreadySentDeliveryRequests = deliveryRequests.filter((d) =>
    DeliveryRequestHelper.isSent(d),
  );

  if (alreadySentDeliveryRequests.length > 0) {
    messages.push(
      deliveryRequestStrings.errorDeliveryRequestsAlreadySent(
        alreadySentDeliveryRequests.map((d) => d.delivery_request_no),
      ),
    );
  }

  if (messages.length > 0) {
    return {
      _tag: 'Left',
      left: { messages },
    };
  }

  return {
    _tag: 'Right',
    right: undefined,
  };
};

export const PICKUP_DATE_ORDER = [
  '-pickup_date',
  'status_pending',
  'status_mail_sent_unread',
  'status_mail_sent_read',
  'status_complete',
].join(',');
